/**
 * Authentication Store
 *
 * Manages authentication state for OAuth and magic link sessions.
 */

import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { getApiClient } from "@/services/api";
import { getOAuthService, isOAuthEnabled } from "@/services/auth";
import {
  getOrCreateGuestSession,
  clearGuestTokens,
} from "@/services/auth/guest";
import { clearAllUserData, validateSessionIntegrity } from "@/lib/session";
import { broadcastLogout } from "@/hooks/useCrossTabLogout";

/**
 * Auth state interface
 */
interface AuthState {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean; // Guard against double logout
  error: string | null;
  userInfo: {
    sub?: string;
    email?: string;
    name?: string;
    contactId?: string; // Contact ID for conversation filtering
  } | null;

  // Actions
  initAuth: () => Promise<void>;
  logout: (reason?: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Auth store
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state - isLoading=true to prevent redirect before initAuth runs
        isAuthenticated: false,
        isLoading: true,
        isLoggingOut: false,
        error: null,
        userInfo: null,

        /**
         * Initialize authentication - checks for existing valid tokens
         */
        initAuth: async () => {
          set({ isLoading: true, error: null });

          try {
            // DEV MODE BYPASS: Always skip auth in development mode
            // Set VITE_DEV_AUTH_BYPASS=false to disable
            const bypassDisabled =
              import.meta.env.VITE_DEV_AUTH_BYPASS === "false";
            if (import.meta.env.DEV && !bypassDisabled) {
              // Debug info available via: console.log('[DEV] Auth bypass active')
              set({
                isAuthenticated: true,
                isLoading: false,
                userInfo: {
                  sub: "dev-user",
                  email: "dev@localhost",
                  name: "Dev User",
                  contactId: "dev-contact-001",
                },
              });
              return;
            }

            // Check if OAuth is enabled and user has valid OAuth tokens
            if (isOAuthEnabled()) {
              const oauthService = getOAuthService();

              if (oauthService.isAuthenticated()) {
                // User has valid OAuth tokens
                try {
                  const userInfo = await oauthService.getUserInfo();
                  set({
                    isAuthenticated: true,
                    isLoading: false,
                    userInfo: {
                      sub: userInfo.sub,
                      email: userInfo.email,
                      name: userInfo.name || userInfo.preferredUsername,
                    },
                  });
                  return;
                } catch (userInfoError) {
                  // Token might be invalid, continue to check other options
                  console.warn("Failed to get user info:", userInfoError);
                }
              }
            }

            // =========================================================
            // LEXE Guest Auth: If OAuth disabled and VITE_TENANT_ID is set,
            // automatically create/restore guest session for instant chat access
            // =========================================================
            if (!isOAuthEnabled() && import.meta.env.VITE_TENANT_ID) {
              console.log("[authStore] LEXE mode: checking guest auth...");

              try {
                // Get or create guest session (checks localStorage first)
                const guestTokens = await getOrCreateGuestSession();

                console.log("[authStore] Guest session active:", {
                  guestId: guestTokens.guestId,
                  contactId: guestTokens.contactId,
                });

                // Store the guest token in ApiClient for API requests
                // Note: ApiClient's internal _saveTokens is private, so we manually store
                localStorage.setItem(
                  "lexe-auth-tokens",
                  JSON.stringify({
                    accessToken: guestTokens.accessToken,
                    expiresAt: guestTokens.expiresAt,
                  }),
                );

                set({
                  isAuthenticated: true,
                  isLoading: false,
                  userInfo: {
                    sub: guestTokens.guestId,
                    name: "Guest",
                    contactId: guestTokens.contactId,
                  },
                });
                return;
              } catch (guestError) {
                console.error("[authStore] Guest auth failed:", guestError);
                // Fall through to check other auth methods
              }
            }

            // Check ApiClient for existing tokens (email/magic link login)
            const client = getApiClient();
            if (client.isAuthenticated()) {
              // Try to extract user info from JWT
              const tokens = localStorage.getItem("lexe-auth-tokens");
              let userInfo = null;

              if (tokens) {
                try {
                  const { accessToken } = JSON.parse(tokens);
                  if (accessToken) {
                    // Decode JWT payload (base64url decode, no verification needed for display)
                    const payload = accessToken.split(".")[1];
                    const decoded = JSON.parse(
                      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
                    );
                    userInfo = {
                      sub: decoded.sub,
                      email: decoded.email,
                      name: decoded.name || decoded.email?.split("@")[0],
                      contactId: decoded.contact_id, // Extract contact_id from JWT for conversation filtering
                    };
                  }
                } catch (e) {
                  console.warn("Failed to decode JWT for user info:", e);
                }
              }

              // =================================================================
              // SECURITY: Do NOT authenticate if contactId is missing (2026-01-20)
              // Principle: A user without contactId cannot have isolated conversations.
              // This prevents data leakage between users.
              // =================================================================
              if (!userInfo?.contactId) {
                console.warn(
                  "[authStore] SECURITY: Token without contact_id - marking as not authenticated",
                );
                // Clear tokens since they're invalid for webchat use
                client.clearTokens();
                set({
                  isAuthenticated: false,
                  isLoading: false,
                  userInfo: null,
                  error: "session_invalid",
                });
                return;
              }

              // =================================================================
              // SECURITY: Validate session integrity (2026-01-21)
              // If localStorage has data from a different user, clear it and force logout
              // This catches cases where user didn't properly logout
              // =================================================================
              if (!validateSessionIntegrity(userInfo.contactId)) {
                console.warn(
                  "[authStore] SECURITY: Session integrity violation - clearing stale data",
                );
                clearAllUserData();
                client.clearTokens();
                set({
                  isAuthenticated: false,
                  isLoading: false,
                  userInfo: null,
                  error: "session_invalid",
                });
                // Redirect to login instead of reload to avoid re-hydration issues
                window.location.href = "/login";
                return;
              }

              set({
                isAuthenticated: true,
                isLoading: false,
                userInfo,
              });
              return;
            }

            // No valid auth found
            set({
              isAuthenticated: false,
              isLoading: false,
            });
          } catch (error) {
            set({
              isAuthenticated: false,
              isLoading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Authentication failed",
            });
          }
        },

        /**
         * Logout and clear session
         *
         * SECURITY FIX (2026-01-21): Clear ALL localStorage FIRST
         * This prevents data leakage between users sharing the same browser
         *
         * @param reason - Optional reason for logout (for logging/debugging)
         */
        logout: async (reason = "user_logout") => {
          // Guard against double logout (can happen with cross-tab sync)
          const { isLoggingOut } = useAuthStore.getState();
          if (isLoggingOut) {
            console.warn("[authStore] Logout already in progress, skipping");
            return;
          }

          set({ isLoggingOut: true });
          console.info(`[authStore] Logout initiated: ${reason}`);

          try {
            // 1. Broadcast to other tabs BEFORE clearing data
            // This ensures other tabs logout even if we crash midway
            broadcastLogout(reason);

            // 2. Reset all Zustand stores in memory (import them dynamically to avoid circular deps)
            const { useChatStore } = await import("@/stores/chatStore");
            const { useMemoryStore } = await import("@/stores/memoryStore");
            const { useAttachmentStore } =
              await import("@/stores/attachmentStore");

            useChatStore.getState().resetStore();
            useMemoryStore.getState().reset();
            useAttachmentStore.getState().reset();

            // 3. Clear ALL user data from localStorage
            // This is the primary defense against cross-user data leakage
            clearAllUserData();

            // 4. Clear API client tokens AND guest tokens
            const client = getApiClient();
            client.clearTokens();
            clearGuestTokens();

            // 5. Clear local auth state in memory
            set({
              isAuthenticated: false,
              userInfo: null,
              error: null,
            });

            // 6. Clear sessionStorage completely
            try {
              sessionStorage.clear();
            } catch (e) {
              console.warn("[authStore] Failed to clear sessionStorage:", e);
            }

            // 7. Redirect to Authentik end-session to terminate SSO session
            if (isOAuthEnabled()) {
              try {
                const oauthService = getOAuthService();
                // redirect: true (default) will navigate to Authentik end-session endpoint
                // This terminates the SSO session completely
                await oauthService.logout({ redirect: true });
                // Note: execution stops here due to redirect
              } catch (e) {
                console.warn("OAuth logout error:", e);
                // Fallback: redirect to login page
                window.location.href = "/login";
              }
            } else {
              // Non-OAuth: redirect to login
              window.location.href = "/login";
            }
          } finally {
            // Reset guard (in case redirect didn't happen)
            set({ isLoggingOut: false });
          }
        },

        /**
         * Clear error message
         */
        clearError: () => {
          set({ error: null });
        },
      }),
      {
        name: "lexe-auth-store",
        // IMPORTANT: Do NOT persist isAuthenticated - it must be validated from tokens
        // Only persist userInfo for display purposes (validated on initAuth)
        partialize: (state) => ({
          userInfo: state.userInfo,
        }),
      },
    ),
    { name: "AuthStore", enabled: import.meta.env.DEV },
  ),
);

// Selectors
export const selectIsAuthenticated = (state: AuthState) =>
  state.isAuthenticated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectIsLoggingOut = (state: AuthState) => state.isLoggingOut;
export const selectAuthError = (state: AuthState) => state.error;
export const selectUserInfo = (state: AuthState) => state.userInfo;

export default useAuthStore;
